import {
  Text,
  Box,
  RadioGroup,
  Flex,
  Avatar,
  Radio,
  InputGroup,
  InputLeftElement,
  Input,
  Icon,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';

import { selectCurrUser } from '@/store/bootstrap/selectors';
import { selectUsers, selectUsersLoading } from '@/store/users/selectors';
import { fetchUsers } from '@/store/users/actions';
import SearchIcon from '@/icons/Search';
import { useSearchList } from '@/hooks/use-search-list';
import NoUsers from '@/assets/icons/colored/NoUsers';

import { useVouchManager } from '../vouch-manager';
import { Loading } from '../../Loading';
import { Link } from 'react-router-dom';
import { useKFlags } from '@/hooks/use-k-flags';

const UsersList = ({ users, onChange = () => {} }) => {
  const { selectedUser } = useVouchManager();
  const [selected, setSelected] = useState(selectedUser);
  const userInput = useRef(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const filteredList = useSearchList({
    list: users,
    searchTerm,
    key: 'name',
  });
  const currUser = useSelector(selectCurrUser);
  const { feOutlook, ats_enabled } = useKFlags();

  useEffect(() => {
    setSelected(selectedUser);
  }, [selectedUser]);

  const OnSearchUser = debounce(() => {
    setSearchTerm(userInput?.current?.value);
  }, 300);

  const onChangeUser = useCallback(
    (s) => {
      onChange(+s);
    },
    [onChange]
  );

  return (
    <>
      <Box>
        <InputGroup>
          <InputLeftElement pointerEvents="none" children={<SearchIcon />} />
          <Input
            placeholder="Search User"
            onChange={OnSearchUser}
            ref={userInput}
          />
        </InputGroup>
      </Box>
      <Box height="470px" overflow="auto" mt="12px">
        {filteredList?.length > 0 ? (
          <RadioGroup name="user" value={selected} onChange={onChangeUser}>
            {filteredList?.map((recruiter) => {
              return (
                <Flex
                  as="label"
                  key={recruiter?.id}
                  _hover={{ bg: '#F6F7F7' }}
                  p="12px"
                  cursor="pointer"
                  borderRadius="8px"
                >
                  <Radio value={recruiter?.id} colorScheme="gray" />
                  <Box ml="12px">
                    <Avatar
                      className="avatar-md"
                      width="36px"
                      height="36px"
                      src={recruiter?.avatar}
                      name={recruiter?.name}
                    />
                  </Box>
                  <Box ml="8px" flex="1">
                    <Text fontWeight="semiBold"> {recruiter?.name}</Text>
                    <Text variant="muted"> {recruiter?.email}</Text>
                  </Box>
                </Flex>
              );
            })}
          </RadioGroup>
        ) : (
          <Box textAlign="center" data-tracking-id="refer-no-users">
            <Icon as={NoUsers} width="200px" height="200px" />
            <Text size="sm-medium" mb="12px">
              No users found
            </Text>
            {currUser.role === 'owner' || currUser.role === 'admin' ? (
              <Text variant="muted">
                Cut the awkward silence.
                <Link
                  to={
                    ats_enabled
                      ? '/settings/profile-integration'
                      : feOutlook
                      ? '/settings/org/integrations'
                      : '/settings/org#integrations'
                  }
                >
                  <Text
                    color="#0043CE"
                    size="sm-bold"
                    as="span"
                    textDecor="underline"
                    p="0 4px"
                  >
                    Click here
                  </Text>
                </Link>
                to invite your colleagues to Kula
              </Text>
            ) : (
              <Text variant="muted">
                It's more fun with people around. Ask your workspace admin to
                invite your colleagues to Kula.
              </Text>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export const VouchToUser = () => {
  const dispatch = useDispatch();
  const currUser = useSelector(selectCurrUser);
  const isLoading = useSelector(selectUsersLoading);
  const users = useSelector(selectUsers);
  const excludeCurrentUser = users?.filter((user) => {
    return user?.id !== currUser?.id;
  });
  const { onSelectUser } = useVouchManager();
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (isLoading) return;
    onSelectUser(excludeCurrentUser?.[0]?.id);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <Loading size={8} />;

  return (
    <Box>
      <UsersList users={excludeCurrentUser} onChange={onSelectUser} />
    </Box>
  );
};
